import { Loader, PasscodeInput } from '@ax/ui';
import { useEffect, useRef, useState } from 'react';
import style from './Login.module.scss';
import useAnalytics from '@/hooks/useAnalytics.tsx';
import { usePasscodeSession } from '@/hooks/usePasscodeSession.tsx';

export interface CreatePasscodeFormProps {
  headerText: string;
  onPasscodeInputCompleted?: (value: string) => void;
  passcode?: string;
  isVerify?: boolean;
  isLoading?: boolean;
  username?: string;
}

export function PasscodeForm({
  onPasscodeInputCompleted,
  isVerify = false,
  passcode,
  headerText,
  username,
  isLoading = false,
}: CreatePasscodeFormProps) {
  const analytics = useAnalytics();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { hasForcePasscodeReset } = usePasscodeSession();
  const sentPINResetAnalytics = useRef(false);

  if (isVerify && !passcode) {
    throw new Error('props.passcode is required for verification');
  }

  useEffect(() => {
    if (!sentPINResetAnalytics.current && !isVerify && hasForcePasscodeReset && username) {
      analytics.onResetPIN({ providerEmail: username });
      sentPINResetAnalytics.current = true;
    }
  }, [analytics, hasForcePasscodeReset, isVerify, username]);

  const handlePasscodeInputCompleted = (value: string) => {
    if (isVerify && passcode !== value) {
      analytics.onVerifyPin({});
      setErrorMessage('PIN does not match');
      return;
    }

    onPasscodeInputCompleted && onPasscodeInputCompleted(value);
  };

  return (
    <div className={style.login__form__body}>
      <div className={style.login__form__body__title}>
        <h1>{headerText}</h1>
      </div>
      <div className={style.login__form__body__subtitle}>
        Your PIN will allow quick access to the app after inactivity. For your security, you'll need
        to re-enter your password every 12 hours.
      </div>
      <div className={style.login__form__body__pin_form}>
        {errorMessage && (
          <div className={style.login__form__body__pin_form__error}>{errorMessage}</div>
        )}
        {!isLoading && (
          <PasscodeInput
            onCompleted={handlePasscodeInputCompleted}
            onChange={() => errorMessage && setErrorMessage(null)}
            hasError={!!errorMessage}
          />
        )}
        {isLoading && (
          <div className={style.login__form__body__pin_form__loading}>
            <Loader color="#344bc6" />
          </div>
        )}
      </div>
    </div>
  );
}
